
import { getChargeAchFees, getChargeAchFeesSync } from '../achFeeSettings';
import {
  STRIPE_FIXED_FEE,
  BANK_STRIPE_FIXED_FEE,
  STRIPE_PERCENT_FEE,
  BANK_STRIPE_PERCENT_FEE,
  KEEP_MORE_PERCENT_FEE
} from '../constants';

// Export this interface so other files can use it
export interface GrossCalculationResult {
  grossAmount: number;
  stripeFee: number;
  keepMoreFee: number; // Changed from platformFee
  applicationFee: number; // Added this field
  netAmount: number;
  totalFees?: number; // Added as optional for backward compatibility
  raw: {
    grossAmountCents: number;
    stripeFeeCents: number;
    keepMoreFeeCents: number; // Changed from platformFeeCents
    applicationFeeCents: number; // Added this field
    netAmountCents: number;
  };
  debug?: any;
}

export const calculateGrossFromNet = async (
  netAmount: number,
  paymentMethod: 'bank' | 'card' = 'card'
): Promise<GrossCalculationResult> => {
  const chargeAchFees = await getChargeAchFees();
  if (paymentMethod === 'bank' && !chargeAchFees) {
    return createNoFeeResult(netAmount);
  }
  return calculateGrossWithCorrection(netAmount, paymentMethod);
};

export const calculateGrossFromNetSync = (
  netAmount: number,
  paymentMethod: 'bank' | 'card' = 'card'
): GrossCalculationResult => {
  const chargeAchFees = getChargeAchFeesSync();
  if (paymentMethod === 'bank' && !chargeAchFees) {
    return createNoFeeResult(netAmount);
  }
  return calculateGrossWithCorrection(netAmount, paymentMethod);
};

function createNoFeeResult(netAmount: number): GrossCalculationResult {
  const netCents = Math.round(netAmount * 100);
  return {
    grossAmount: netAmount,
    stripeFee: 0,
    keepMoreFee: 0, // Changed from platformFee
    applicationFee: 0, // Added this field
    netAmount,
    raw: {
      grossAmountCents: netCents,
      stripeFeeCents: 0,
      keepMoreFeeCents: 0, // Changed from platformFeeCents
      applicationFeeCents: 0, // Added this field
      netAmountCents: netCents
    },
    debug: {
      note: 'ACH fees not charged',
      exactMatch: true
    }
  };
}

function calculateGrossWithCorrection(
  netAmount: number,
  paymentMethod: 'bank' | 'card'
): GrossCalculationResult {
  const netCents = Math.round(netAmount * 100);

  const stripePercent = paymentMethod === 'bank' ? BANK_STRIPE_PERCENT_FEE : STRIPE_PERCENT_FEE;
  const stripeFixed = paymentMethod === 'bank' ? BANK_STRIPE_FIXED_FEE : STRIPE_FIXED_FEE;
  const keepMorePercent = paymentMethod === 'bank' ? 0 : KEEP_MORE_PERCENT_FEE;

  const stripeFixedCents = Math.round(stripeFixed * 100);
  const denominator = 1 - stripePercent - keepMorePercent;
  let grossCents = Math.round((netCents + stripeFixedCents) / denominator);

  let delta = 1;
  let iteration = 0;
  const maxIterations = 20;
  const debug: any[] = [];

  while (iteration < maxIterations) {
    const stripeFeeCents = Math.round(grossCents * stripePercent) + stripeFixedCents;
    const keepMoreFeeCents = Math.round(grossCents * keepMorePercent); // Changed variable name
    const computedNetCents = grossCents - stripeFeeCents - keepMoreFeeCents;
    delta = netCents - computedNetCents;

    debug.push({ 
      iteration, 
      grossCents, 
      stripeFeeCents, 
      keepMoreFeeCents, // Changed from platformFeeCents
      computedNetCents, 
      delta 
    });

    if (delta === 0) break;
    grossCents += delta; // Jump full delta, not just +/-1
    iteration++;
  }

  const stripeFeeCents = Math.round(grossCents * stripePercent) + stripeFixedCents;
  const keepMoreFeeCents = Math.round(grossCents * keepMorePercent); // Changed variable name
  const applicationFeeCents = keepMoreFeeCents; // Set to same value
  const netAmountCents = grossCents - stripeFeeCents - keepMoreFeeCents;

  return {
    grossAmount: grossCents / 100,
    stripeFee: stripeFeeCents / 100,
    keepMoreFee: keepMoreFeeCents / 100, // Changed from platformFee
    applicationFee: applicationFeeCents / 100, // Added applicationFee field
    netAmount: netCents / 100,
    raw: {
      grossAmountCents: grossCents,
      stripeFeeCents,
      keepMoreFeeCents, // Changed from platformFeeCents
      applicationFeeCents, // Added applicationFeeCents field
      netAmountCents
    },
    debug: {
      iterations: debug,
      exactMatch: delta === 0
    }
  };
}
